// §6.1 / §14 — the skins the shell can mount.
//
// The kernel never imports this (§6.5); the shell does, and hands the chosen skin down. F13:
// a skin that fails `validateSkin` at runtime falls back to house.
import type { SkinId } from '@hangar/shared';
import { houseSkin } from './house';
import { windows11Skin } from './windows11';
import { macosSkin } from './macos';
import { validateSkin, describeSkinProblems, type UiSkin } from './contract';

export const SKINS: Record<SkinId, UiSkin | undefined> = {
  house: houseSkin,
  windows11: windows11Skin,
  macos: macosSkin,
};

export const FALLBACK_SKIN = houseSkin;

export function availableSkins(): UiSkin[] {
  return Object.values(SKINS).filter((skin): skin is UiSkin => skin !== undefined);
}

export interface SkinResolution {
  skin: UiSkin;
  /** Set when the requested skin was missing or invalid and house was used instead (F13). */
  fallbackReason: string | null;
}

export function resolveSkin(id: SkinId): SkinResolution {
  const requested = SKINS[id];
  if (!requested) {
    return {
      skin: FALLBACK_SKIN,
      fallbackReason: id === FALLBACK_SKIN.id ? null : `the ${id} skin is not available`,
    };
  }

  const validation = validateSkin(requested);
  if (!validation.ok) {
    return {
      skin: FALLBACK_SKIN,
      fallbackReason: `the ${id} skin failed validation: ${describeSkinProblems(validation)}`,
    };
  }

  return { skin: requested, fallbackReason: null };
}
